import * as SentryUtils from "./sentry";
import {
  CardRenderer,
  generateExportCard,
} from "../../../shared/utils/cardRenderer";

// Re-export types from shared utilities
export type { CardData } from "../../../shared/utils/cardRenderer";

// Helper function to detect mobile devices
const isMobileDevice = (): boolean => {
  return (
    /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent,
    ) ||
    (navigator.maxTouchPoints !== undefined && navigator.maxTouchPoints > 2)
  );
};

const getDisplayWidth = (): number => {
  if (typeof window === "undefined") return 400; // SSR fallback
  return Math.min(window.innerWidth - 40, 1200);
};

// Generate card for display (smaller, window-based size)
export const generateDisplayCard = async (
  data: { userName: string },
  backgroundImageUrl: string,
): Promise<HTMLCanvasElement> => {
  const displayWidth = getDisplayWidth();
  const renderer = new CardRenderer({
    maxWidth: displayWidth,
  });

  try {
    return await renderer.generateCard(data, backgroundImageUrl);
  } catch (error) {
    SentryUtils.captureException(error, {
      tags: { component: "cardGenerator", operation: "generate_card" }
    });
    throw error;
  }
};

// Generate card for export (high resolution) and handle download/share
export const downloadCard = async (
  data: { userName: string },
  backgroundImageUrl: string,
  filename: string = "membership-card.png",
): Promise<void> => {
  try {
    const canvas = await generateExportCard(data, backgroundImageUrl);

    // Convert canvas to blob
    const blob = await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob((blob) => {
        if (blob) {
          resolve(blob);
        } else {
          reject(new Error("Failed to generate card blob"));
        }
      }, "image/png");
    });

    // Use Web Share API only on mobile devices
    if (
      isMobileDevice() &&
      navigator.share &&
      navigator.canShare &&
      navigator.canShare({
        files: [new File([blob], filename, { type: blob.type })],
      })
    ) {
      try {
        await navigator.share({
          files: [new File([blob], filename, { type: blob.type })],
          title: "Membership Card",
          text: "CO2 Snowboarding membership card",
        });
        return; // Success - don't fallback to download
      } catch (error) {
        // Check if user dismissed the share menu (common on iOS)
        if (
          error instanceof Error &&
          (error.name === "AbortError" ||
            error.name === "NotAllowedError" ||
            error.message.includes("User cancel") ||
            error.message.includes("cancelled") ||
            error.message.includes("dismissed"))
        ) {
          console.log("User dismissed share menu");
          return; // User deliberately dismissed - don't fallback to download
        }

        console.log("Web Share API failed with real error:", error);
      }
    }

    // Use traditional download for desktop or when share is not available/fails with real error
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  } catch (error) {
    SentryUtils.captureException(error, {
      tags: { component: "cardGenerator", operation: "generate_card" }
    });
    throw error;
  }
};

// For backward compatibility, export a CardGenerator class that uses the shared renderer
export class CardGenerator {
  private renderer: CardRenderer;
  private backgroundImageUrl: string;

  constructor(options: { maxWidth?: number; backgroundImageUrl: string }) {
    this.backgroundImageUrl = options.backgroundImageUrl;
    this.renderer = new CardRenderer({
      maxWidth: options.maxWidth || 600,
    });
  }

  public async generateCard(data: {
    userName: string;
  }): Promise<HTMLCanvasElement> {
    return this.renderer.generateCard(data, this.backgroundImageUrl);
  }

  public async generateCardBlob(
    data: { userName: string },
    format: "png" | "jpeg" = "png",
    quality?: number,
  ): Promise<Blob> {
    return this.renderer.generateCardBlob(
      data,
      this.backgroundImageUrl,
      format,
      quality,
    );
  }
}
